import { type MouseEvent, useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdministratorActionsMenu } from "../AdministratorActionsMenu";
import { getAdministratorAccountTypeLabel, getAdministratorFullName } from "../administratorUtils";
import type {
  AdministratorActionHandler,
  AggregatorAdministrator,
  TranslationFunction,
} from "../types";

interface AggregatorAdministratorsTableProps {
  administrators?: AggregatorAdministrator[];
  isLoading?: boolean;
  onClickDeleteAdministrator: AdministratorActionHandler;
  onClickEditAdministrator: AdministratorActionHandler;
  onClickEnableAdministrator: AdministratorActionHandler;
  t: TranslationFunction;
  warningMessage?: string;
}

const ADMINISTRATORS_PAGE_SIZE = 10;

export const AggregatorAdministratorsTable = ({
  administrators = [],
  isLoading,
  onClickDeleteAdministrator,
  onClickEditAdministrator,
  onClickEnableAdministrator,
  t,
  warningMessage,
}: AggregatorAdministratorsTableProps) => {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.max(1, Math.ceil(administrators.length / ADMINISTRATORS_PAGE_SIZE));
  const pageStart = pageIndex * ADMINISTRATORS_PAGE_SIZE;
  const pageAdministrators = administrators.slice(pageStart, pageStart + ADMINISTRATORS_PAGE_SIZE);
  const hasAdministrators = administrators.length > 0;
  const columnCount = 5;

  useEffect(() => {
    setPageIndex(currentPageIndex => Math.min(currentPageIndex, pageCount - 1));
  }, [pageCount]);

  const onClickPage = (event: MouseEvent<HTMLAnchorElement>, nextPageIndex: number) => {
    event.preventDefault();
    setPageIndex(Math.min(Math.max(nextPageIndex, 0), pageCount - 1));
  };

  if (warningMessage && !hasAdministrators && !isLoading) {
    return (
      <Alert variant="warning" className="mb-8">
        <TriangleAlert className="size-4" />
        <AlertDescription>{warningMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="mb-8 space-y-3">
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>{t("aggregatorDetails.administratorModal.accountType")}</TableHead>
              <TableHead>{t("aggregatorDetails.administratorModal.email")}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Loading isVisible small />
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && !hasAdministrators ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                  {t("aggregatorDetails.administratorModal.noAdminsAdded")}
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && hasAdministrators
              ? pageAdministrators.map((administrator, index) => (
                  <TableRow key={administrator.id || administrator.email || index}>
                    <TableCell className="font-medium text-foreground">
                      {getAdministratorFullName(administrator)}
                    </TableCell>
                    <TableCell>
                      {getAdministratorAccountTypeLabel(administrator.account_type, t)}
                    </TableCell>
                    <TableCell>{administrator.email || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={administrator.active ? "success" : "secondary"}>
                        {administrator.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <AdministratorActionsMenu
                        administrator={administrator}
                        onClickDeleteAdministrator={onClickDeleteAdministrator}
                        onClickEditAdministrator={onClickEditAdministrator}
                        onClickEnableAdministrator={onClickEnableAdministrator}
                        t={t}
                      />
                    </TableCell>
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
      {pageCount > 1 ? (
        <Pagination className="justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                disabled={pageIndex === 0}
                onClick={event => onClickPage(event, pageIndex - 1)}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-3 text-muted-foreground text-sm">
                {pageIndex + 1} / {pageCount}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                disabled={pageIndex >= pageCount - 1}
                onClick={event => onClickPage(event, pageIndex + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
};

export type { AggregatorAdministratorsTableProps };
